const INVOICE_NAMESPACE = "http://www.forum-datenaustausch.ch/invoice";
const GCR_NAMESPACE = "http://sumex1.net/gcr";
const XSI_NAMESPACE = "http://www.w3.org/2001/XMLSchema-instance";

export type DocumentVersion = '450' | '500' | 'unknown';

export interface PartyInfo {
	name?: string;
	companyName?: string;
	department?: string;
	street?: string;
	streetName?: string;
	houseNo?: string;
	poBox?: string;
	zip?: string;
	city?: string;
	country?: string;
	countryCode?: string;
	stateCode?: string;
	email?: string;
	url?: string;
	phone?: string;
	fax?: string;
	eanParty?: string;
	gln?: string;
	glnLocation?: string;
	zsr?: string;
	nifNumber?: string;
	uidNumber?: string;
	salutation?: string;
	title?: string;
	familyName?: string;
	givenName?: string;
	specialty?: string;
	subaddressing?: string;
}

export interface PatientInfo extends PartyInfo {
	gender?: string;
	birthdate?: string;
	ahvNumber?: string;
	vekaNumber?: string;
	insuredNumber?: string;
	accidentDate?: string;
	accidentNumber?: string;
	cardId?: string;
	patientId?: string;
}

export interface DiagnosisInfo {
	type?: string;
	code?: string;
	text?: string;
}

export interface StationaryInfo {
	hospitalizationType?: string;
	hospitalizationMode?: string;
	hospitalizationClass?: string;
	sectionMajor?: string;
}

export interface CantonalPhysicianData {
	responsibleAtHospitalWard?: string;
	usedTariffType?: string;
	usedBaserate?: string;
	additionallyRegisteredAtAv?: boolean;
	additionallyRegisteredAtIv?: boolean;
	isMedicalNecessity?: boolean;
	justificationForService?: string;
	locationOfEmergency?: string;
	dateOfEmergency?: string;
	justificationForReconsideration?: string;
}

export interface DocumentAttachment {
	filename?: string;
	mimeType?: string;
	/** 4.50 only, removed in 5.00 */
	title?: string;
	/** 5.00 only, required there */
	documentType?: string;
	base64Content?: string;
}

export interface TreatmentInfo {
	dateBegin?: string;
	dateEnd?: string;
	canton?: string;
	reason?: string;
	diagnoses?: DiagnosisInfo[];
	stationary?: StationaryInfo;
	data4CantonalPhysician?: CantonalPhysicianData;
	apid?: string;
	acid?: string;
	treatmentType?: string;
	admissionType?: string;
	insuranceClass?: string;
	department?: string;
}

export interface ServiceLine {
	recordId: string;
	code: string;
	name: string;
	quantity: number;
	unit: number;
	unitFactor: number;
	amount: number;
	date?: string;
	dateEnd?: string;
	vatRate?: number;
	tariffType?: string;
	session?: string;
	providerId?: string;
	responsibleId?: string;
	externalFactor?: number;
	serviceAttributes?: string;
	obligation?: string;
	referenceNumber?: string;
	remark?: string;
	unitMt?: number;
	unitFactorMt?: number;
	amountMt?: number;
	unitTt?: number;
	unitFactorTt?: number;
	amountTt?: number;
	bodyLocation?: string;
	sectionCode?: string;
}

export interface VatInfo {
	amount: number;
	rate: number;
	number?: string;
}

export interface BankInfo extends PartyInfo {
	clearingNumber?: string;
	giroAccount?: string;
}

export type EsrVariant = 'esr9' | 'esrRed' | 'esrQR' | 'esrQRRed';

export interface EsrInfo {
	variant: EsrVariant;
	/** 4.50 esr9/esrRed @type */
	type?: string;
	/** 5.00 esrQR/esrQRRed @subtype (4.50 esrQR used @type) */
	subtype?: string;
	participantNumber?: string;
	referenceNumber?: string;
	codingLine?: string;
	iban?: string;
	paymentReason?: string;
	paymentPeriod?: string;
	alternativeScheme1?: string;
	alternativeScheme2?: string;
	/** 4.50 only, removed in 5.00 */
	customerNote?: string;
	creditor?: PartyInfo;
	/** 4.50 only, removed in 5.00 */
	bank?: BankInfo;
}

export interface Partner {
	type: string;
	typeTitle?: string;
	gln?: string;
	zsr?: string;
	specialty?: string;
	person?: PartyInfo;
	company?: PartyInfo;
}

export interface EmployerInfo extends PartyInfo {
	regNumber?: string;
}

export interface DocumentData {
	type: 'lieferschein' | 'kostenvoranschlag' | 'unknown';
	version: DocumentVersion;
	documentNumber: string;
	date: string;
	timestamp?: string;
	requestTimestamp?: string;
	validationStatus?: string;
	release?: string;
	law?: string;
	lawType?: string;
	caseId?: string;
	caseDate?: string;
	role?: string;
	roleTitle?: string;
	place?: string;

	// Parties
	biller?: PartyInfo;
	patient?: PatientInfo;
	insurance?: PartyInfo;
	debitor?: PartyInfo;
	provider?: PartyInfo;
	guarantor?: PartyInfo;
	referrer?: PartyInfo;
	employer?: EmployerInfo;
	partners?: Partner[];

	// Treatment
	treatment?: TreatmentInfo;

	// Services
	services: ServiceLine[];
	servicesCount: number;
	servicesTotal?: number;

	// Financial
	subtotal: number;
	vat?: VatInfo;
	total: number;
	currency: string;
	paymentTerms?: string;
	amountDue?: number;
	amountObligations?: number;
	medicalAmount?: number;
	medicationAmount?: number;
	laboratoryAmount?: number;
	migelAmount?: number;
	otherAmount?: number;
	prepaidAmount?: number;

	// Payment
	esr?: EsrInfo;
	tiersType?: 'tiers_garant' | 'tiers_payant' | 'tiers_soldant';

	// Metadata
	language?: string;
	modus?: string;
	transportFrom?: string;
	transportTo?: string;
	transportVia?: string;
	transportSequenceId?: string;
	printAtIntermediate?: boolean;
	printCopyToGuarantor?: boolean;
	printPatientInvoiceOnly?: boolean;
	isConfidential?: boolean;
	isCopy?: boolean;
	isStorno?: boolean;
	isCostEstimate?: boolean;
	remark?: string;
	measures?: string;

	// Package/Generator info
	packageName?: string;
	packageVersion?: string;
	packageCopyright?: string;
	generatorName?: string;
	generatorVersion?: string;

	// Request info
	requestType?: string;
	requestSubtype?: string;
	guid?: string;
	creditRequestId?: string;
	creditRequestDate?: string;

	// Additional fields for complete document display
	prescriptionType?: string;
	insuranceClass?: string;
	departmentIndication?: string;
	surgeon?: PartyInfo;
	documents?: DocumentAttachment[];
	reminderDate?: string;
	reminderNumber?: string;
}

export class DocumentParser {
	static parseXmlDocument(xmlString: string): DocumentData | null {
		try {
			const parser = new DOMParser();
			const doc = parser.parseFromString(xmlString, "text/xml");

			if (doc.getElementsByTagName("parsererror").length > 0) {
				console.error("XML parsing error");
				return null;
			}

			const version = this.detectVersion(doc);

			switch (this.detectDocumentKind(doc)) {
				case 'invoice':
					return this.parseInvoiceDocument(doc, version);
				case 'gcr':
					return this.parseGcrDocument(doc, version);
				default:
					return null;
			}
		} catch (error) {
			console.error("Error parsing XML document:", error);
			return null;
		}
	}

	private static detectDocumentKind(doc: Document): 'invoice' | 'gcr' | null {
		const root = doc.documentElement;

		if (root.namespaceURI === INVOICE_NAMESPACE) return 'invoice';
		if (root.namespaceURI === GCR_NAMESPACE) return 'gcr';

		if (root.tagName === "invoice:request") return 'invoice';
		if (root.tagName === "gcr:request") return 'gcr';

		if (root.localName !== "request") return null;
		if (this.first(doc, "invoice")) return 'invoice';
		if (this.first(doc, "credit")) return 'gcr';
		return null;
	}

	/**
	 * 4.50 and 5.00 share the same target namespace, so the namespace alone cannot tell
	 * them apart. Prefer the schema location hint, fall back to structural markers.
	 */
	private static detectVersion(doc: Document): DocumentVersion {
		const root = doc.documentElement;
		const schemaLocation =
			root.getAttributeNS(XSI_NAMESPACE, "schemaLocation") ||
			root.getAttribute("xsi:schemaLocation") ||
			'';

		if (schemaLocation.includes("_500.xsd")) return '500';
		if (schemaLocation.includes("_450.xsd")) return '450';

		if (root.hasAttribute("guid") || this.first(doc, "law")) return '500';

		const payloadEl = this.first(doc, "payload");
		if (payloadEl?.hasAttribute("type")) return '450';
		if (this.first(doc, "kvg", "vvg", "uvg", "ivg", "mvg", "org")) return '450';

		return 'unknown';
	}

	private static parseInvoiceDocument(doc: Document, version: DocumentVersion): DocumentData {
		const data: DocumentData = {
			type: 'lieferschein',
			version,
			documentNumber: '',
			date: '',
			services: [],
			servicesCount: 0,
			subtotal: 0,
			total: 0,
			currency: 'CHF'
		};

		const rootEl = doc.documentElement;
		data.language = rootEl.getAttribute("language") || undefined;
		data.modus = rootEl.getAttribute("modus") || undefined;
		data.validationStatus = rootEl.getAttribute("validation_status") || undefined;
		data.guid = rootEl.getAttribute("guid") || undefined;

		const creditEl = this.first(doc, "credit");
		if (creditEl) {
			data.creditRequestId = creditEl.getAttribute("request_id") || undefined;
			data.creditRequestDate = this.formatDate(creditEl.getAttribute("request_date") || '');
		}

		const invoiceEl = this.first(doc, "invoice");
		if (invoiceEl) {
			data.documentNumber = invoiceEl.getAttribute("request_id") || '';
			data.date = this.formatDate(invoiceEl.getAttribute("request_date") || '');
			data.timestamp = invoiceEl.getAttribute("request_timestamp") || undefined;
			data.requestTimestamp = invoiceEl.getAttribute("request_timestamp") || undefined;
		}

		const reminderEl = this.first(doc, "reminder");
		if (reminderEl) {
			data.reminderNumber = reminderEl.getAttribute("reminder_level") || reminderEl.getAttribute("request_id") || undefined;
			data.reminderDate = this.formatDate(reminderEl.getAttribute("request_date") || '');
		}

		const bodyEl = this.first(doc, "body");
		if (bodyEl) {
			data.role = bodyEl.getAttribute("role") || undefined;
			data.roleTitle = bodyEl.getAttribute("role_title") || undefined;
			data.place = bodyEl.getAttribute("place") || undefined;
		}

		const processingEl = this.first(doc, "processing");
		if (processingEl) {
			data.printAtIntermediate = processingEl.getAttribute("print_at_intermediate") === "1";
			data.printCopyToGuarantor = this.optionalFlag(processingEl, "print_copy_to_guarantor");
			data.printPatientInvoiceOnly = this.optionalFlag(processingEl, "print_patient_invoice_only");
		}

		const transportEl = this.first(doc, "transport");
		if (transportEl) {
			data.transportFrom = transportEl.getAttribute("from") || undefined;
			data.transportTo = transportEl.getAttribute("to") || undefined;

			const viaEl = this.first(transportEl, "via");
			if (viaEl) {
				data.transportVia = viaEl.getAttribute("via") || undefined;
				data.transportSequenceId = viaEl.getAttribute("sequence_id") || undefined;
			}
		}

		const payloadEl = this.first(doc, "payload");
		if (payloadEl) {
			// 5.00 uses request_type/request_subtype, 4.50 used a single type attribute
			data.requestType = this.attr(payloadEl, "request_type", "type");
			data.requestSubtype = payloadEl.getAttribute("request_subtype") || undefined;
			data.isCopy = payloadEl.getAttribute("copy") === "1";
			data.isStorno = payloadEl.getAttribute("storno") === "1";
		}

		const packageEl = this.first(doc, "package");
		if (packageEl) {
			data.packageName = packageEl.getAttribute("name") || undefined;
			data.packageVersion = packageEl.getAttribute("version") || undefined;
			data.packageCopyright = packageEl.getAttribute("copyright") || undefined;
		}

		const generatorEl = this.first(doc, "generator");
		if (generatorEl) {
			data.generatorName = generatorEl.getAttribute("name") || undefined;
			data.generatorVersion = generatorEl.getAttribute("version") || undefined;
		}

		data.remark = this.text(doc, "remark");

		const tiersEl = this.first(doc, "tiers_garant", "tiers_soldant", "tiers_payant");
		if (tiersEl) {
			data.tiersType = tiersEl.localName as DocumentData['tiersType'];
			data.paymentTerms = tiersEl.getAttribute("payment_period") || undefined;
		}

		// 5.00 nests billers/providers by identifier kind; 4.50 had a flat biller/provider
		const billersEl = this.first(doc, "billers");
		if (billersEl) {
			const billerEl = this.first(billersEl, "biller_gln", "biller_zsr");
			data.biller = this.parseParty(billerEl);
		} else {
			data.biller = this.parseParty(this.first(doc, "biller"));
		}

		const providersEl = this.first(doc, "providers");
		if (providersEl) {
			const providerEl = this.first(providersEl, "provider_gln", "provider_zsr");
			data.provider = this.parseParty(providerEl);
		} else {
			data.provider = this.parseParty(this.first(doc, "provider"));
		}

		data.patient = this.parsePatient(this.first(doc, "patient"));
		data.insurance = this.parseParty(this.first(doc, "insurance"));
		data.debitor = this.parseParty(this.first(doc, "debitor"));
		data.guarantor = this.parseParty(this.first(doc, "guarantor"));

		// 5.00 replaced referrer/employer with a generic partners list
		const partnersEl = this.first(doc, "partners");
		if (partnersEl) {
			data.partners = this.all(partnersEl, "partner").map(el => {
				const partner: Partner = {
					type: el.getAttribute("type") || '',
					typeTitle: el.getAttribute("type_title") || undefined,
					gln: el.getAttribute("gln") || undefined,
					zsr: el.getAttribute("zsr") || undefined,
					specialty: el.getAttribute("specialty") || undefined
				};

				const party = this.parseParty(el);
				if (this.first(el, "person")) partner.person = party;
				if (this.first(el, "company")) partner.company = party;

				return partner;
			});

			data.referrer = data.partners.find(p => p.type === 'referrer')?.person ?? data.partners.find(p => p.type === 'referrer')?.company;
			const employerPartner = data.partners.find(p => p.type === 'employer');
			data.employer = employerPartner?.company ?? employerPartner?.person;
		} else {
			data.referrer = this.parseParty(this.first(doc, "referrer"));
			data.employer = this.parseEmployer(this.first(doc, "employer"));
		}

		const treatmentEl = this.first(doc, "treatment");
		if (treatmentEl) {
			data.treatment = {
				dateBegin: this.formatDate(treatmentEl.getAttribute("date_begin") || ''),
				dateEnd: this.formatDate(treatmentEl.getAttribute("date_end") || ''),
				canton: treatmentEl.getAttribute("canton") || undefined,
				reason: treatmentEl.getAttribute("reason") || undefined,
				treatmentType: treatmentEl.getAttribute("treatment") || undefined,
				apid: treatmentEl.getAttribute("apid") || undefined,
				acid: treatmentEl.getAttribute("acid") || undefined
			};

			const diagnosisEls = this.all(treatmentEl, "diagnosis");
			if (diagnosisEls.length > 0) {
				data.treatment.diagnoses = diagnosisEls.map(el => ({
					type: el.getAttribute("type") || undefined,
					code: el.getAttribute("code") || undefined,
					text: el.textContent?.trim() || undefined
				}));
			}
		}

		const lawEl = this.first(doc, "law");
		if (lawEl) {
			data.lawType = lawEl.getAttribute("type") || undefined;
			data.law = data.lawType;
			data.caseId = lawEl.getAttribute("case_id") || undefined;
			data.caseDate = this.formatDate(lawEl.getAttribute("case_date") || '');
			if (data.patient) {
				data.patient.insuredNumber = lawEl.getAttribute("insured_id") || data.patient.insuredNumber;
				data.patient.cardId = lawEl.getAttribute("card_id") || data.patient.cardId;
			}
		} else {
			// 4.50 encoded the law as a dedicated element
			const legacyLawEl = this.first(doc, "kvg", "vvg", "uvg", "ivg", "mvg", "org");
			if (legacyLawEl) {
				data.law = legacyLawEl.localName.toUpperCase();
				data.caseId = legacyLawEl.getAttribute("case_id") || undefined;
				data.caseDate = this.formatDate(legacyLawEl.getAttribute("case_date") || '');
			}
		}

		data.services = this.parseServices(doc);
		data.servicesCount = data.services.length;

		const balanceEl = this.first(doc, "balance");
		if (balanceEl) {
			data.total = parseFloat(balanceEl.getAttribute("amount") || '0');
			data.amountDue = parseFloat(balanceEl.getAttribute("amount_due") || '0');
			data.amountObligations = parseFloat(balanceEl.getAttribute("amount_obligations") || '0');
			data.currency = balanceEl.getAttribute("currency") || 'CHF';
			const prepaid = balanceEl.getAttribute("amount_prepaid");
			if (prepaid) data.prepaidAmount = parseFloat(prepaid);
		}

		const vatEl = this.first(doc, "vat");
		if (vatEl) {
			data.vat = {
				amount: parseFloat(vatEl.getAttribute("vat") || '0'),
				rate: parseFloat(this.first(vatEl, "vat_rate")?.getAttribute("vat_rate") || '0'),
				number: vatEl.getAttribute("vat_number") || undefined
			};
			data.subtotal = data.total - data.vat.amount;
		} else {
			data.subtotal = data.total;
		}

		data.esr = this.parseEsr(doc);
		data.documents = this.parseDocuments(doc);

		return data;
	}

	private static parseGcrDocument(doc: Document, version: DocumentVersion): DocumentData {
		const data: DocumentData = {
			type: 'kostenvoranschlag',
			version,
			documentNumber: '',
			date: '',
			services: [],
			servicesCount: 0,
			subtotal: 0,
			total: 0,
			currency: 'CHF'
		};

		const rootEl = doc.documentElement;
		data.language = rootEl.getAttribute("language") || undefined;
		data.modus = rootEl.getAttribute("modus") || undefined;
		data.isConfidential = rootEl.getAttribute("is_confidential") === "1";
		data.validationStatus = rootEl.getAttribute("validation_status") || undefined;

		const creditEl = this.first(doc, "credit");
		if (creditEl) {
			data.documentNumber = creditEl.getAttribute("request_id") || '';
			data.date = this.formatDate(creditEl.getAttribute("request_date") || '');
			data.timestamp = creditEl.getAttribute("request_timestamp") || undefined;
			data.requestTimestamp = creditEl.getAttribute("request_timestamp") || undefined;
		}

		const bodyEl = this.first(doc, "body");
		if (bodyEl) {
			data.role = bodyEl.getAttribute("role") || undefined;
			data.roleTitle = bodyEl.getAttribute("role_title") || undefined;
			data.place = bodyEl.getAttribute("place") || undefined;
		}

		const processingEl = this.first(doc, "processing");
		if (processingEl) {
			data.printAtIntermediate = processingEl.getAttribute("print_at_intermediate") === "1";
			data.printCopyToGuarantor = processingEl.getAttribute("print_copy_to_guarantor") === "1";
		}

		const transportEl = this.first(doc, "transport");
		if (transportEl) {
			data.transportFrom = transportEl.getAttribute("from") || undefined;
			data.transportTo = transportEl.getAttribute("to") || undefined;

			const viaEl = this.first(transportEl, "via");
			if (viaEl) {
				data.transportVia = viaEl.getAttribute("via") || undefined;
				data.transportSequenceId = viaEl.getAttribute("sequence_id") || undefined;
			}
		}

		const payloadEl = this.first(doc, "payload");
		if (payloadEl) {
			data.requestType = payloadEl.getAttribute("type") || undefined;
			data.isCopy = payloadEl.getAttribute("copy") === "1";
			data.isStorno = payloadEl.getAttribute("storno") === "1";
			data.isCostEstimate = payloadEl.getAttribute("cost_estimate") === "1";
		}

		const packageEl = this.first(doc, "package");
		if (packageEl) {
			data.packageName = packageEl.getAttribute("name") || undefined;
			data.packageVersion = packageEl.getAttribute("version") || undefined;
			data.packageCopyright = packageEl.getAttribute("copyright") || undefined;
		}

		const generatorEl = this.first(doc, "generator");
		if (generatorEl) {
			data.generatorName = generatorEl.getAttribute("name") || undefined;
			data.generatorVersion = generatorEl.getAttribute("version") || undefined;
		}

		data.biller = this.parseParty(this.first(doc, "biller"));
		data.provider = this.parseParty(this.first(doc, "provider"));
		data.patient = this.parsePatient(this.first(doc, "patient"));
		data.insurance = this.parseParty(this.first(doc, "insurance"));
		data.debitor = this.parseParty(this.first(doc, "debitor"));
		data.referrer = this.parseParty(this.first(doc, "referrer"));
		data.surgeon = this.parseParty(this.first(doc, "surgeon"));
		data.employer = this.parseEmployer(this.first(doc, "employer"));

		data.measures = this.text(doc, "measures");
		data.remark = this.text(doc, "remark");

		const treatmentEl = this.first(doc, "treatment");
		if (treatmentEl) {
			data.treatment = {
				dateBegin: this.formatDate(treatmentEl.getAttribute("date_begin") || ''),
				dateEnd: this.formatDate(treatmentEl.getAttribute("date_end") || ''),
				canton: this.attr(treatmentEl, "treatment_canton", "canton"),
				reason: this.attr(treatmentEl, "treatment_reason", "reason")
			};

			const diagnosisEls = this.all(treatmentEl, "diagnosis");
			if (diagnosisEls.length > 0) {
				data.treatment.diagnoses = diagnosisEls.map(el => ({
					type: el.getAttribute("type") || undefined,
					code: el.getAttribute("code") || undefined,
					text: el.textContent?.trim() || undefined
				}));
			}

			const stationaryEl = this.first(treatmentEl, "stationary");
			if (stationaryEl) {
				data.treatment.stationary = {
					hospitalizationType: stationaryEl.getAttribute("hospitalization_type") || undefined,
					hospitalizationMode: stationaryEl.getAttribute("hospitalization_mode") || undefined,
					hospitalizationClass: stationaryEl.getAttribute("hospitalization_class") || undefined,
					sectionMajor: stationaryEl.getAttribute("section_major") || undefined
				};
			}

			const data4cpEl = this.first(treatmentEl, "data4CantonalPhysician");
			if (data4cpEl) {
				data.treatment.data4CantonalPhysician = {
					responsibleAtHospitalWard: data4cpEl.getAttribute("responsible_at_hospital_ward") || undefined,
					usedTariffType: data4cpEl.getAttribute("used_tariff_type") || undefined,
					usedBaserate: data4cpEl.getAttribute("used_baserate") || undefined,
					additionallyRegisteredAtAv: data4cpEl.getAttribute("additionally_registered_at_av") === "1",
					additionallyRegisteredAtIv: data4cpEl.getAttribute("additionally_registered_at_iv") === "1",
					isMedicalNecessity: data4cpEl.getAttribute("is_medical_necessity") === "1",
					justificationForService: data4cpEl.getAttribute("justification_for_service") || undefined,
					locationOfEmergency: data4cpEl.getAttribute("location_of_emergency") || undefined,
					dateOfEmergency: this.formatDate(data4cpEl.getAttribute("date_of_emergency") || ''),
					justificationForReconsideration: data4cpEl.getAttribute("justification_for_reconsideration") || undefined
				};
			}
		}

		const legacyLawEl = this.first(doc, "kvg", "vvg", "uvg", "ivg", "mvg", "org");
		if (legacyLawEl) {
			data.law = legacyLawEl.localName.toUpperCase();
			data.caseId = legacyLawEl.getAttribute("case_id") || undefined;
			data.caseDate = this.formatDate(legacyLawEl.getAttribute("case_date") || '');
			if (data.patient) {
				data.patient.insuredNumber = legacyLawEl.getAttribute("insured_id") || data.patient.insuredNumber;
				data.patient.ahvNumber = legacyLawEl.getAttribute("ssn") || data.patient.ahvNumber;
			}
		}

		const servicesEl = this.first(doc, "services");
		if (servicesEl) {
			data.servicesTotal = parseFloat(servicesEl.getAttribute("amount") || '0');
			data.servicesCount = parseInt(servicesEl.getAttribute("number") || '0');
		}

		data.services = this.all(doc, "service").map(el => ({
			recordId: el.getAttribute("record_id") || '',
			code: el.getAttribute("code") || '',
			name: el.getAttribute("name") || '',
			quantity: parseFloat(el.getAttribute("quantity") || '1'),
			unit: parseFloat(el.getAttribute("unit") || '0'),
			unitFactor: parseFloat(el.getAttribute("unit_factor") || '1'),
			amount: parseFloat(el.getAttribute("amount") || '0'),
			date: this.formatDate(el.getAttribute("date") || ''),
			dateEnd: this.formatDate(el.getAttribute("date_end") || ''),
			tariffType: el.getAttribute("tariff_type") || undefined,
			session: el.getAttribute("session") || undefined,
			providerId: el.getAttribute("provider_id") || undefined,
			responsibleId: el.getAttribute("responsible_id") || undefined,
			externalFactor: parseFloat(el.getAttribute("external_factor") || '1'),
			serviceAttributes: el.getAttribute("service_attributes") || undefined,
			obligation: el.getAttribute("obligation") || undefined,
			referenceNumber: el.getAttribute("ref_code") || undefined,
			remark: el.getAttribute("remark") || undefined
		}));

		data.documents = this.parseDocuments(doc);

		data.total = data.servicesTotal || data.services.reduce((sum, s) => sum + s.amount, 0);
		data.subtotal = data.total; // No VAT in cost estimate

		return data;
	}

	private static parseServices(doc: Document): ServiceLine[] {
		const servicesEl = this.first(doc, "services");
		const serviceEls = servicesEl
			? Array.from(servicesEl.children).filter(el => el.localName === "service" || el.localName === "service_ex")
			: [...this.all(doc, "service"), ...this.all(doc, "service_ex")];

		return serviceEls.map(el => ({
			recordId: el.getAttribute("record_id") || '',
			code: el.getAttribute("code") || '',
			name: el.getAttribute("name") || '',
			quantity: parseFloat(el.getAttribute("quantity") || '1'),
			unit: parseFloat(el.getAttribute("unit") || '0'),
			unitFactor: parseFloat(el.getAttribute("unit_factor") || '1'),
			amount: parseFloat(el.getAttribute("amount") || '0'),
			date: this.formatDate(el.getAttribute("date_begin") || ''),
			dateEnd: this.formatDate(el.getAttribute("date_end") || ''),
			vatRate: parseFloat(el.getAttribute("vat_rate") || '0'),
			tariffType: el.getAttribute("tariff_type") || undefined,
			session: el.getAttribute("session") || undefined,
			providerId: el.getAttribute("provider_id") || undefined,
			responsibleId: el.getAttribute("responsible_id") || undefined,
			externalFactor: parseFloat(el.getAttribute("external_factor") || '1'),
			serviceAttributes: el.getAttribute("service_attributes") || undefined,
			obligation: el.getAttribute("obligation") || undefined,
			referenceNumber: el.getAttribute("ref_code") || undefined,
			remark: el.getAttribute("remark") || undefined,
			unitMt: parseFloat(el.getAttribute("unit_mt") || '0'),
			unitFactorMt: parseFloat(el.getAttribute("unit_factor_mt") || '1'),
			amountMt: parseFloat(el.getAttribute("amount_mt") || '0'),
			unitTt: parseFloat(el.getAttribute("unit_tt") || '0'),
			unitFactorTt: parseFloat(el.getAttribute("unit_factor_tt") || '1'),
			amountTt: parseFloat(el.getAttribute("amount_tt") || '0'),
			bodyLocation: el.getAttribute("body_location") || undefined,
			sectionCode: el.getAttribute("section_code") || undefined
		}));
	}

	private static parseEsr(doc: Document): EsrInfo | undefined {
		const el = this.first(doc, "esrQR", "esrQRRed", "esr9", "esrRed");
		if (!el) return undefined;

		const esr: EsrInfo = {
			variant: el.localName as EsrVariant,
			type: el.getAttribute("type") || undefined,
			// 5.00 renamed @type to @subtype on the QR variants
			subtype: this.attr(el, "subtype", "type"),
			participantNumber: el.getAttribute("participant_number") || undefined,
			referenceNumber: el.getAttribute("reference_number") || undefined,
			codingLine: el.getAttribute("coding_line") || undefined,
			iban: el.getAttribute("iban") || undefined,
			// 5.00 turned the payment_reason child element into an attribute
			paymentReason: el.getAttribute("payment_reason") || this.text(el, "payment_reason"),
			paymentPeriod: el.getAttribute("payment_period") || undefined,
			alternativeScheme1: el.getAttribute("alternative_scheme1") || undefined,
			alternativeScheme2: el.getAttribute("alternative_scheme2") || undefined,
			customerNote: el.getAttribute("customer_note") || undefined,
			creditor: this.parseParty(this.first(el, "creditor"))
		};

		const bankEl = this.first(el, "bank");
		if (bankEl) {
			esr.bank = {
				...this.parseParty(bankEl),
				clearingNumber: bankEl.getAttribute("clearing_number") || undefined,
				giroAccount: bankEl.getAttribute("giro_account") || undefined
			};
		}

		return esr;
	}

	private static parseDocuments(doc: Document): DocumentAttachment[] | undefined {
		const documentsEl = this.first(doc, "documents");
		if (!documentsEl) return undefined;

		const documentEls = this.all(documentsEl, "document");
		if (documentEls.length === 0) return undefined;

		return documentEls.map(el => ({
			filename: el.getAttribute("filename") || undefined,
			mimeType: el.getAttribute("mimeType") || undefined,
			title: el.getAttribute("title") || undefined,
			documentType: el.getAttribute("documentType") || undefined,
			base64Content: this.first(el, "base64")?.textContent || undefined
		}));
	}

	/**
	 * Parses any party wrapper (biller, provider, patient, partner, creditor, …). The wrapper
	 * carries the identifiers as attributes and holds either a company or a person child.
	 */
	private static parseParty(element: Element | null): PartyInfo | undefined {
		if (!element) return undefined;

		const info: PartyInfo = {
			gln: element.getAttribute("gln") || undefined,
			glnLocation: element.getAttribute("gln_location") || undefined,
			eanParty: element.getAttribute("ean_party") || undefined,
			zsr: element.getAttribute("zsr") || undefined,
			uidNumber: element.getAttribute("uid_number") || undefined,
			nifNumber: element.getAttribute("nif_number") || undefined,
			specialty: element.getAttribute("specialty") || undefined
		};

		const companyEl = this.first(element, "company");
		if (companyEl) {
			info.companyName = this.text(companyEl, "companyname");
			info.department = this.text(companyEl, "department");
			info.name = info.companyName;
			this.applyPostal(info, this.first(companyEl, "postal"));
			this.applyContact(info, companyEl);
		}

		const personEl = this.first(element, "person");
		if (personEl) {
			info.salutation = personEl.getAttribute("salutation") || undefined;
			info.title = personEl.getAttribute("title") || undefined;
			info.familyName = this.text(personEl, "familyname");
			info.givenName = this.text(personEl, "givenname");
			info.name = [info.givenName, info.familyName].filter(Boolean).join(' ') || undefined;
			info.subaddressing = this.text(personEl, "subaddressing");
			this.applyPostal(info, this.first(personEl, "postal"));
			this.applyContact(info, personEl);
		}

		return info;
	}

	private static parseEmployer(element: Element | null): EmployerInfo | undefined {
		const info = this.parseParty(element);
		if (!info || !element) return undefined;

		return { ...info, regNumber: element.getAttribute("reg_number") || undefined };
	}

	private static parsePatient(element: Element | null): PatientInfo | undefined {
		if (!element) return undefined;

		return {
			...this.parseParty(element),
			gender: this.attr(element, "gender", "sex"),
			birthdate: this.formatDate(element.getAttribute("birthdate") || ''),
			ahvNumber: element.getAttribute("ssn") || undefined,
			insuredNumber: element.getAttribute("insured_id") || undefined
		};
	}

	private static applyPostal(info: PartyInfo, postalEl: Element | null): void {
		if (!postalEl) return;

		const streetEl = this.first(postalEl, "street");
		const zipEl = this.first(postalEl, "zip");
		const countryEl = this.first(postalEl, "country");

		info.street = streetEl?.textContent?.trim() || undefined;
		info.streetName = streetEl?.getAttribute("street_name") || undefined;
		info.houseNo = streetEl?.getAttribute("house_no") || undefined;
		info.poBox = this.text(postalEl, "pobox");
		info.zip = zipEl?.textContent?.trim() || undefined;
		info.city = this.text(postalEl, "city");
		// 5.00 renamed @statecode to @state_code and added the country element
		info.stateCode = this.attr(zipEl, "state_code", "statecode");
		info.country = countryEl?.textContent?.trim() || undefined;
		info.countryCode = countryEl?.getAttribute("country_code") || this.attr(zipEl, "countrycode");
	}

	private static applyContact(info: PartyInfo, containerEl: Element): void {
		const onlineEl = this.first(containerEl, "online");
		if (onlineEl) {
			info.email = this.text(onlineEl, "email");
			// 5.00 renamed online/url to online/uri
			info.url = this.text(onlineEl, "uri", "url");
		}

		const telecomEl = this.first(containerEl, "telecom");
		if (telecomEl) {
			info.phone = this.text(telecomEl, "phone");
			// fax exists in 4.50 only
			info.fax = this.text(telecomEl, "fax");
		}
	}

	/** Namespace-agnostic lookup: returns the first descendant matching any of the local names, in order of preference. */
	private static first(scope: Document | Element, ...localNames: string[]): Element | null {
		for (const localName of localNames) {
			const found = scope.getElementsByTagNameNS("*", localName)[0];
			if (found) return found;
		}
		return null;
	}

	private static all(scope: Document | Element, localName: string): Element[] {
		return Array.from(scope.getElementsByTagNameNS("*", localName));
	}

	private static text(scope: Document | Element, ...localNames: string[]): string | undefined {
		return this.first(scope, ...localNames)?.textContent?.trim() || undefined;
	}

	private static attr(element: Element | null, ...names: string[]): string | undefined {
		if (!element) return undefined;
		for (const name of names) {
			const value = element.getAttribute(name);
			if (value) return value;
		}
		return undefined;
	}

	private static optionalFlag(element: Element, name: string): boolean | undefined {
		const value = element.getAttribute(name);
		if (value === null) return undefined;
		return value === "1";
	}

	private static formatDate(isoDate: string): string {
		if (!isoDate) return '';

		try {
			const date = new Date(isoDate);
			if (isNaN(date.getTime())) return isoDate;

			return date.toLocaleDateString("de-CH", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric"
			});
		} catch {
			return isoDate;
		}
	}
}
